import { useCallback, useMemo } from 'react';

import { TAB_DEFAULT_BASE_NAME } from '@/features/dashboard/constants/names';
import { useMoveMenuCallbacks } from '@/features/dashboard/pages/Edit/hooks/useMoveMenuCallbacks';
import { getTabNameError } from '@/features/dashboard/state/lib/invariants';
import type { MarketAppWithTabs, WidgetV2 } from '@/features/dashboard/types';
import { useAppSelector } from '@/store/hooks';

import {
  selectCurrentTabId,
  selectInitialDashboardName
} from '@/features/dashboard/state/dashboardEdit.selectors';
import { useDashboardEditActions } from '../../../hooks/useDashboardEditActions';
import { DashboardTargetMenu } from '../../menus';
import { useMoveTargets } from '../hooks';

import type { TabInfo } from '../../menus';

// ============================================================================
// Types
// ============================================================================

interface MoveMenuBaseProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onRemoveFromCurrent: () => void;
}

interface MoveAppProps extends MoveMenuBaseProps {
  variant: 'app';
  app: MarketAppWithTabs;
  item?: never;
  appId?: never;
  currentDashboardName?: never;
  currentTabId?: never;
  onTabCreated?: never;
}

interface MoveItemProps extends MoveMenuBaseProps {
  variant: 'item';
  item: WidgetV2;
  appId: string;
  currentDashboardName: string;
  currentTabId: string;
  onTabCreated?: (tabName: string, tabId: string) => void;
  app?: never;
}

export type MoveMenuProps = MoveAppProps | MoveItemProps;

// ============================================================================
// Component
// ============================================================================

/**
 * Unified menu for moving apps or items to another dashboard/tab.
 *
 * Replaces the duplicate MoveAppToDashboardMenu and MoveItemToDashboardMenu
 * by consolidating shared logic into a single component with a variant prop.
 *
 * @example
 * ```tsx
 * // Move entire app
 * <MoveMenu
 *   variant="app"
 *   app={app}
 *   anchorEl={anchorEl}
 *   open={open}
 *   onClose={onClose}
 *   onRemoveFromCurrent={handleRemove}
 * />
 *
 * // Move single item
 * <MoveMenu
 *   variant="item"
 *   item={item}
 *   appId={appId}
 *   currentDashboardName={dashboardName}
 *   currentTabId={tabId}
 *   anchorEl={anchorEl}
 *   open={open}
 *   onClose={onClose}
 *   onRemoveFromCurrent={handleRemove}
 *   onTabCreated={handleTabCreated}
 * />
 * ```
 */
export const MoveMenu = (props: MoveMenuProps) => {
  const { variant, anchorEl, open, onClose, onRemoveFromCurrent } = props;

  const currentTabIdFromRedux = useAppSelector(selectCurrentTabId);
  const initialDashboardName = useAppSelector(selectInitialDashboardName);
  const actions = useDashboardEditActions();

  // Determine current tab ID based on variant
  const currentTabId = variant === 'item' ? props.currentTabId : currentTabIdFromRedux;

  const { dashboardNamesList, effectiveCurrentDashboardName, getDashboardTabs } = useMoveTargets({
    currentTabId: currentTabId || null
  });

  const { handleCreateDashboard, handleError } = useMoveMenuCallbacks({
    createDashboardDraft: actions.createUniqueDashboardDraft
  });

  // Validate names based on variant
  const isMoveDisabled = useMemo(() => {
    if (variant === 'app') {
      // Validate all tab names in the app
      return props.app.tabs.some(tab => {
        const error = getTabNameError(tab.name, []);
        return error !== undefined;
      });
    }
    // Validate single item name
    const error = getTabNameError(props.item.name, []);
    return error !== undefined;
  }, [props, variant]);

  /**
   * Handle moving to an existing tab.
   */
  const handleSelectTab = useCallback(
    (targetDashboard: string, tab: TabInfo) => {
      if (variant === 'app') {
        actions.moveAppToOtherDashboard(targetDashboard, tab.tabId, props.app);
      } else {
        actions.moveAppTabToOtherDashboard(targetDashboard, tab.tabId, props.item);
      }
      onRemoveFromCurrent();
      onClose();
    },
    [variant, props, actions, onRemoveFromCurrent, onClose]
  );

  /**
   * Handle creating a new tab and moving to it.
   */
  const handleCreateNewTab = useCallback(
    (targetDashboard: string) => {
      const tabId = crypto.randomUUID();
      const tabName = TAB_DEFAULT_BASE_NAME;

      if (variant === 'app') {
        actions.ensureTabExistsInOtherDashboard(targetDashboard, tabId, tabName);
        actions.moveAppToOtherDashboard(targetDashboard, tabId, props.app);
      } else {
        const isCurrentDashboard =
          targetDashboard === effectiveCurrentDashboardName ||
          targetDashboard === initialDashboardName;

        if (isCurrentDashboard && props.onTabCreated) {
          props.onTabCreated(tabName, tabId);
        } else {
          actions.ensureTabExistsInOtherDashboard(targetDashboard, tabId, tabName);
        }

        actions.moveAppTabToOtherDashboard(targetDashboard, tabId, props.item);
      }

      onRemoveFromCurrent();
      onClose();
    },
    [
      variant,
      props,
      actions,
      effectiveCurrentDashboardName,
      initialDashboardName,
      onRemoveFromCurrent,
      onClose
    ]
  );

  return (
    <DashboardTargetMenu
      showChevron
      showNewPageOption
      anchorEl={anchorEl}
      currentDashboardName={effectiveCurrentDashboardName}
      dashboards={dashboardNamesList}
      disabled={isMoveDisabled}
      excludeCurrentTabId={currentTabId}
      getTabs={getDashboardTabs}
      open={open}
      variant='with-tabs'
      onClose={onClose}
      onCreateDashboard={handleCreateDashboard}
      onCreateNewTab={handleCreateNewTab}
      onError={handleError}
      onSelectTab={handleSelectTab}
    />
  );
};

export default MoveMenu;
